import { MONTHS_EL, MONTHS_EL_SHORT } from "./greek"
import { getLocale, intlLocale, localized } from "./i18n"
import type { DeliveryMethod, Queue } from "./medusa"

/**
 * Numbers, money and dates, written the way the chosen language writes them:
 * English €1,234.50 · 29 Sep, 18:00 — Greek 1.234,50 € · 29 Σεπ, 18:00. Times are always 24-hour and
 * always Athens time, wherever the phone is.
 */

const TIME_ZONE = "Europe/Athens"
const formats = new Map<string, Intl.NumberFormat | Intl.DateTimeFormat>()

function numberFormat(options: Intl.NumberFormatOptions, locale = intlLocale()): Intl.NumberFormat {
  const key = `n|${locale}|${JSON.stringify(options)}`
  let format = formats.get(key) as Intl.NumberFormat | undefined
  if (!format) formats.set(key, (format = new Intl.NumberFormat(locale, options)))
  return format
}

function dateFormat(options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = `d|${intlLocale()}|${JSON.stringify(options)}`
  let format = formats.get(key) as Intl.DateTimeFormat | undefined
  if (!format) formats.set(key, (format = new Intl.DateTimeFormat(intlLocale(), { timeZone: TIME_ZONE, hourCycle: "h23", ...options })))
  return format
}

// English money keeps the Irish euro format it always had (€1,234.50); Greek writes 1.234,50 €.
const moneyLocale = () => (getLocale() === "el" ? "el-GR" : "en-IE")

// Medusa v2 returns money in MAJOR units (60 for €60), so these format the number as-is.
export const money = (amount: number) => numberFormat({ style: "currency", currency: "EUR" }, moneyLocale()).format(amount)

// Headline figures read better without the cents (€4,514 rather than €4,514.00).
export const money0 = (amount: number) => numberFormat({ style: "currency", currency: "EUR", maximumFractionDigits: 0 }, moneyLocale()).format(Math.round(amount))

/** A number in the language's own way: 1,234.5 / 1.234,5. Not for order numbers or years. */
export const formatNumber = (value: number, options: Intl.NumberFormatOptions = { maximumFractionDigits: 2 }) => numberFormat(options).format(value)

/** A share as a percentage: formatPercent(0.443) → 44%. */
export const formatPercent = (ratio: number, digits = 0) =>
  numberFormat({ style: "percent", minimumFractionDigits: digits, maximumFractionDigits: digits }).format(ratio)

type DateInput = Date | string | number

const asDate = (value: DateInput) => (value instanceof Date ? value : new Date(value))

const PRESETS = {
  /** 29 Sep · 29 Σεπ */
  day: { day: "numeric", month: "short" },
  /** 29 Sep 2026 · 29 Σεπ 2026 */
  dayYear: { day: "numeric", month: "short", year: "numeric" },
  /** Tue 29 Sep · Τρί 29 Σεπ */
  weekday: { weekday: "short", day: "numeric", month: "short" },
  /** Tuesday 29 September · Τρίτη 29 Σεπτεμβρίου */
  weekdayLong: { weekday: "long", day: "numeric", month: "long" },
  /** 29/09/2026 */
  numeric: { day: "2-digit", month: "2-digit", year: "numeric" },
  /** September 2026 · Σεπτέμβριος 2026 */
  month: { month: "long", year: "numeric" },
  /** Sep 2026 · Σεπ 2026 */
  monthShort: { month: "short", year: "numeric" },
  /** 18:00 */
  time: { hour: "2-digit", minute: "2-digit" },
  /** 29 Sep, 18:00 · 29 Σεπ, 18:00 */
  dayTime: { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" },
} satisfies Record<string, Intl.DateTimeFormatOptions>

export type DatePreset = keyof typeof PRESETS

/** A date in one of HQ's usual shapes (see PRESETS), in Athens time. Empty for no date. */
export function formatDate(value: DateInput | null | undefined, preset: DatePreset = "day"): string {
  if (value === null || value === undefined || value === "") return ""
  const date = asDate(value)
  if (Number.isNaN(date.getTime())) return ""
  return dateFormat(PRESETS[preset]).format(date)
}

/** A date with any Intl options, in the current language and Athens time — for shapes PRESETS lacks. */
export function formatDateWith(value: DateInput, options: Intl.DateTimeFormatOptions): string {
  return dateFormat(options).format(asDate(value))
}

/**
 * A month's name on its own (a chart's axis, a month picker): September / Σεπτέμβριος. Greek needs its
 * own list, because Intl gives the form used inside a date (Σεπτεμβρίου) for a month standing alone.
 * Takes a date, or a month index 0–11.
 */
export function monthName(value: DateInput, form: "long" | "short" = "long"): string {
  const index = typeof value === "number" && value >= 0 && value < 12 ? value : Number(dateFormat({ month: "numeric" }).format(asDate(value))) - 1
  if (getLocale() === "el") return (form === "long" ? MONTHS_EL : MONTHS_EL_SHORT)[index]
  return new Intl.DateTimeFormat("en-GB", { month: form, timeZone: "UTC" }).format(new Date(Date.UTC(2000, index, 15)))
}

/** How long ago: "3h ago" / «πριν από 3 ώρες»; past a month, the date. */
export function ago(iso: string | null) {
  if (!iso) return ""
  const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60000)
  if (getLocale() === "el") {
    const relative = new Intl.RelativeTimeFormat("el", { numeric: "auto" })
    if (minutes < 1) return "μόλις τώρα"
    if (minutes < 60) return relative.format(-minutes, "minute")
    const hours = Math.round(minutes / 60)
    if (hours < 24) return relative.format(-hours, "hour")
    const days = Math.round(hours / 24)
    return days < 30 ? relative.format(-days, "day") : formatDate(iso, "day")
  }
  if (minutes < 1) return "just now"
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.round(hours / 24)
  return days < 30 ? `${days}d ago` : formatDate(iso, "day")
}

export const dateTime = (iso: string | null) => (iso ? formatDate(iso, "dayTime") : "")

/** "and" between the last two of a list, the language's way: a, b and c / α, β και γ. */
export function formatList(items: string[]): string {
  return new Intl.ListFormat(getLocale() === "el" ? "el" : "en-GB", { type: "conjunction" }).format(items)
}

export const DELIVERY_LABEL: Record<DeliveryMethod, string> = localized(
  {
    box_now: "BOX NOW",
    home: "Home delivery",
    meetup: "Meet-up",
    unknown: "Other",
  },
  "delivery"
)

export const QUEUE_LABEL: Record<Queue, string> = localized(
  {
    to_ship: "To ship",
    to_arrange: "To arrange",
    cash_to_collect: "Cash to collect",
    done: "Done",
    canceled: "Cancelled",
  },
  "queue"
)

export const QUEUE_HINT: Record<Queue, string> = localized({
  to_ship: "BOX NOW orders waiting for a voucher.",
  to_arrange: "Meet-ups and home deliveries to arrange on Instagram.",
  cash_to_collect: "Cash orders whose money is not recorded yet.",
  done: "Nothing left to do.",
  canceled: "Cancelled orders.",
})
